import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PlusCircle, Info, ClipboardList, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, FieldLabel, Pills, Stat } from "@/components/Bits";
import { SearchSelect } from "@/components/SearchSelect";
import { TrioInputs } from "@/components/TrioInputs";
import { useApp } from "@/store/AppStore";
import { SYSTEMS, BOLT_TYPES, getBoltDiameters, getBoltLengths, getRate } from "@/data/boltData";
import { NUT_SPECS, getNutSizes, getNutRate } from "@/data/nutData";
import { computeTrio, gramsPerPiece, rateFromGrams, washerRate } from "@/lib/calc";
import { fmtNum, fmtInt, fmtBags, fmtDia, fmtLen, fmtNutSize, boltFullLabel, boltShortLabel, boltTypeName } from "@/lib/format";

const firstTypeOf = (system) => BOLT_TYPES.find((t) => t.system === system)?.id;
const WASHER_MODES = [
  { value: "pcsbag", label: "PCS / BAG" },
  { value: "wtpc", label: "WT/PC (g)" },
  { value: "kg100", label: "KG / 100" },
];

export function AssemblyPage() {
  const { addLineToOrder, createOrder, orders } = useApp();
  const [bolt, setBolt] = useState({ system: "inches", typeId: "in_full", dia: "", len: "" });
  const [metricSpec, setMetricSpec] = useState("old");
  const [nutSize, setNutSize] = useState("");
  const [boltOvG, setBoltOvG] = useState("");
  const [nutOvG, setNutOvG] = useState("");
  const [washer, setWasher] = useState({ enabled: false, count: "2", mode: "pcsbag", input: "", size: "" });
  const [field, setField] = useState("sets");
  const [raw, setRaw] = useState("");
  const [pending, setPending] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const nutSpec = bolt.system === "inches" ? "bsw" : metricSpec;

  // auto-pair nut size to bolt diameter when valid
  useEffect(() => {
    if (!bolt.dia) return;
    const sizes = getNutSizes(nutSpec);
    setNutSize(sizes.includes(bolt.dia) ? bolt.dia : "");
  }, [bolt.dia, nutSpec]);

  // default washer size to bolt diameter label
  useEffect(() => {
    if (bolt.dia) setWasher((w) => ({ ...w, size: w.size || fmtDia(bolt.system, bolt.dia) }));
  }, [bolt.dia, bolt.system]);

  const boltRate = getRate(bolt.typeId, bolt.dia, bolt.len);
  const nutRate = getNutRate(nutSpec, nutSize);
  const wRate = washer.enabled ? washerRate(washer.mode, washer.input) : 0;

  const boltWtKg = boltOvG ? Number(boltOvG) / 1000 : boltRate ? 50 / boltRate : 0;
  const nutWtKg = nutOvG ? Number(nutOvG) / 1000 : nutRate ? 50 / nutRate : 0;
  const washerWtKg = wRate ? 50 / wRate : 0;
  const count = Math.max(0, Number(washer.count) || 0);
  const setWtKg = boltWtKg + nutWtKg + (washer.enabled ? count * washerWtKg : 0);
  const ready = boltRate && nutRate && setWtKg > 0;

  // synced order qty (weight / bags / sets)
  const qty = useMemo(() => {
    const v = Number(raw) || 0;
    let sets;
    if (field === "sets") sets = v;
    else if (field === "bags") sets = setWtKg ? (v * 50) / setWtKg : 0;
    else sets = setWtKg ? v / setWtKg : 0;
    const weight = sets * setWtKg;
    return { sets, weight, bags: weight / 50 };
  }, [field, raw, setWtKg]);

  const sets = qty.sets;
  const out = {
    boltPcs: sets, boltKg: sets * boltWtKg,
    nutPcs: sets, nutKg: sets * nutWtKg,
    washerPcs: sets * count, washerKg: sets * count * washerWtKg,
  };

  const diaOpts = getBoltDiameters(bolt.typeId).map((d) => ({ value: d, label: fmtDia(bolt.system, d) }));
  const lenOpts = getBoltLengths(bolt.typeId, bolt.dia).map((l) => ({ value: l, label: fmtLen(bolt.system, l) }));
  const nutOpts = getNutSizes(nutSpec).map((s) => ({ value: s, label: fmtNutSize(nutSpec, s), sub: `${getNutRate(nutSpec, s)}/50kg` }));
  const typeOpts = BOLT_TYPES.filter((t) => t.system === bolt.system).map((t) => ({ value: t.id, label: t.name }));

  const setSystem = (system) => setBolt({ system, typeId: firstTypeOf(system), dia: "", len: "" });
  const setType = (typeId) => setBolt((b) => ({ ...b, typeId, dia: "", len: "" }));

  const qtyFields = [
    { key: "weight", label: "Weight", unit: "KG", testid: "asm-input-weight", fmt: (n) => fmtNum(n, 2) },
    { key: "bags", label: "Bags", unit: "BAG", testid: "asm-input-bags", fmt: (n) => fmtNum(n, 2) },
    { key: "sets", label: "Sets", unit: "SET", testid: "asm-input-sets", fmt: (n) => fmtInt(n) },
  ];

  const buildLine = () => {
    if (!ready) {
      toast.error("Complete bolt + nut selection");
      return null;
    }
    const setsInt = Math.round(sets);
    if (setsInt < 1) {
      toast.error("Enter order quantity");
      return null;
    }
    return {
      sets: setsInt,
      assemblyText: `1 BOLT + 1 NUT${washer.enabled ? ` + ${count} WASHER` : ""}`,
      bolt: {
        key: `${bolt.typeId}|${bolt.dia}|${bolt.len}`,
        label: boltFullLabel(bolt),
        size: boltShortLabel(bolt),
        typeName: boltTypeName(bolt.typeId),
        pcs: setsInt, kg: setsInt * boltWtKg, bags: (setsInt * boltWtKg) / 50,
      },
      nut: {
        key: `${nutSpec}|${nutSize}`,
        label: `${fmtNutSize(nutSpec, nutSize)} · ${NUT_SPECS.find((s) => s.id === nutSpec)?.label}`,
        size: fmtNutSize(nutSpec, nutSize),
        pcs: setsInt, kg: setsInt * nutWtKg, bags: (setsInt * nutWtKg) / 50,
      },
      washer: washer.enabled
        ? {
            enabled: true, key: washer.size || "washer", label: washer.size || "Washer", count,
            pcs: setsInt * count, kg: setsInt * count * washerWtKg, bags: (setsInt * count * washerWtKg) / 50,
          }
        : { enabled: false },
    };
  };

  const openPicker = () => {
    const line = buildLine();
    if (line) {
      setPending(line);
      setNewName("");
      setPickerOpen(true);
    }
  };

  const commit = async (orderId, orderName) => {
    const res = await addLineToOrder(orderId, pending);
    if (!res) return;
    toast.success(`Added ${pending.sets} sets → ${orderName}`);
    setPickerOpen(false);
    setPending(null);
    setRaw(""); // clear sheet, keep selections
    setField("sets");
  };

  const commitNewOrder = async () => {
    const name = newName.trim() || `Order ${orders.length + 1}`;
    const order = await createOrder(name);
    if (order) await commit(order.id, order.name);
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-3 text-sm font-bold text-white">🔩 Bolt</div>
        <div className="space-y-3">
          <Pills testid="asm-system" value={bolt.system} onChange={setSystem} size="sm" options={SYSTEMS.map((s) => ({ value: s.id, label: s.label }))} />
          <Pills testid="asm-type" value={bolt.typeId} onChange={setType} size="sm" options={typeOpts} />
          <div className="grid grid-cols-2 gap-3">
            <SearchSelect testid="asm-diameter" value={bolt.dia} onChange={(dia) => setBolt((b) => ({ ...b, dia, len: "" }))} options={diaOpts} placeholder="Diameter" />
            <SearchSelect testid="asm-length" value={bolt.len} onChange={(len) => setBolt((b) => ({ ...b, len }))} options={lenOpts} placeholder="Length" disabled={!bolt.dia} />
          </div>
          <OverrideRow label="Bolt WT/PC" ovG={boltOvG} setOvG={setBoltOvG} catalog={gramsPerPiece(boltRate)} testid="asm-bolt-override" />
        </div>
      </Card>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-bold text-white">⬡ Nut <span className="text-xs font-normal text-slate-500">(auto-paired)</span></div>
          {bolt.system === "metric" && (
            <Pills testid="asm-metric-spec" size="sm" value={metricSpec} onChange={setMetricSpec} options={[{ value: "old", label: "OLD" }, { value: "new", label: "NEW" }]} />
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center rounded-xl border border-slate-700 bg-slate-900/50 px-3 text-sm font-semibold text-slate-300" data-testid="asm-nut-spec-label">
            {NUT_SPECS.find((s) => s.id === nutSpec)?.label}
          </div>
          <SearchSelect testid="asm-nut-size" value={nutSize} onChange={setNutSize} options={nutOpts} placeholder="Nut size" />
        </div>
        <div className="mt-3">
          <OverrideRow label="Nut WT/PC" ovG={nutOvG} setOvG={setNutOvG} catalog={gramsPerPiece(nutRate)} testid="asm-nut-override" />
        </div>
      </Card>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-bold text-white">⭘ Washers</div>
          <Switch data-testid="asm-washer-toggle" checked={washer.enabled} onCheckedChange={(v) => setWasher((w) => ({ ...w, enabled: v }))} />
        </div>
        {washer.enabled && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1.5 block text-[11px] font-semibold uppercase text-slate-400">Count / Set</span>
                <Input data-testid="asm-washer-count" inputMode="numeric" value={washer.count} onChange={(e) => setWasher((w) => ({ ...w, count: e.target.value }))} className="h-11 rounded-xl border-slate-700 bg-slate-900/60 text-right font-mono text-white" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-[11px] font-semibold uppercase text-slate-400">Washer Size</span>
                <Input data-testid="asm-washer-size" value={washer.size} onChange={(e) => setWasher((w) => ({ ...w, size: e.target.value }))} placeholder="e.g. M12" className="h-11 rounded-xl border-slate-700 bg-slate-900/60 text-white" />
              </label>
            </div>
            <div>
              <FieldLabel>Rate Type</FieldLabel>
              <Pills testid="asm-washer-mode" size="sm" value={washer.mode} onChange={(mode) => setWasher((w) => ({ ...w, mode }))} options={WASHER_MODES} />
            </div>
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase text-slate-400">
                {WASHER_MODES.find((m) => m.value === washer.mode)?.label} value
              </span>
              <Input data-testid="asm-washer-input" inputMode="decimal" value={washer.input} onChange={(e) => setWasher((w) => ({ ...w, input: e.target.value }))} placeholder="0" className="h-11 rounded-xl border-slate-700 bg-slate-900/60 text-right font-mono text-white" />
              {wRate > 0 && <span className="mt-1 block text-right font-mono text-[11px] text-cyan-400">≈ {fmtInt(wRate)} pcs / 50kg</span>}
            </label>
          </div>
        )}
      </Card>

      <Card className={ready ? "border-orange-500/40" : ""}>
        <FieldLabel right={ready && <span className="font-mono text-xs text-cyan-400">1 set ≈ {fmtNum(setWtKg * 1000, 0)} g</span>}>Order Quantity</FieldLabel>
        <TrioInputs fields={qtyFields} values={qty} field={field} raw={raw} onEdit={(k, v) => { setField(k); setRaw(v); }} disabled={!ready} />

        <div className="mt-4 space-y-2">
          <OutRow label="BOLT" size={boltShortLabel(bolt)} pcs={out.boltPcs} kg={out.boltKg} testid="asm-out-bolt" />
          <OutRow label="NUT" size={fmtNutSize(nutSpec, nutSize)} pcs={out.nutPcs} kg={out.nutKg} testid="asm-out-nut" />
          {washer.enabled && <OutRow label="WASHER" size={washer.size} pcs={out.washerPcs} kg={out.washerKg} testid="asm-out-washer" />}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <Stat label="Total Sets" value={fmtInt(sets)} testid="asm-total-sets" accent />
          <Stat label="Total Bags" value={fmtBags(qty.bags)} testid="asm-total-bags" />
        </div>

        <Button data-testid="add-to-order-btn" onClick={openPicker} disabled={!ready} className="mt-4 w-full rounded-xl bg-orange-500 py-6 text-base font-extrabold text-slate-950 hover:bg-orange-400 disabled:opacity-40">
          <PlusCircle className="mr-2 h-5 w-5" /> ADD TO ORDER
        </Button>
      </Card>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="border-slate-700 bg-[#131C2E] text-slate-100" data-testid="order-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <ClipboardList className="h-5 w-5 text-orange-400" /> Add to which order?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Pick an existing order or create a new one for this set.
            </DialogDescription>
          </DialogHeader>

          {pending && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2 text-sm">
              <span className="font-mono font-bold text-cyan-400">{pending.bolt.size}</span>
              <span className="mx-2 text-slate-600">·</span>
              <span className="text-slate-300">{pending.assemblyText}</span>
              <span className="mx-2 text-slate-600">·</span>
              <span className="font-mono font-bold text-orange-300">{fmtInt(pending.sets)} sets</span>
            </div>
          )}

          <div className="max-h-56 space-y-2 overflow-y-auto">
            {orders.length === 0 && (
              <p className="py-2 text-center text-sm text-slate-500">No orders yet — create one below.</p>
            )}
            {orders.map((o) => (
              <button
                key={o.id}
                data-testid={`picker-order-${o.id}`}
                onClick={() => commit(o.id, o.name)}
                className="flex w-full items-center justify-between rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-3 text-left transition-colors hover:border-orange-500/50 hover:bg-slate-800/60"
              >
                <span className="font-semibold text-slate-100">{o.name}</span>
                <span className="text-xs text-slate-500">{o.lines.length} line{o.lines.length !== 1 ? "s" : ""}</span>
              </button>
            ))}
          </div>

          <div className="flex gap-2 border-t border-slate-800 pt-3">
            <Input
              data-testid="picker-new-order-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && commitNewOrder()}
              placeholder={`New order name (e.g. Order ${orders.length + 1})`}
              className="h-11 rounded-xl border-slate-700 bg-slate-900/60 text-white"
            />
            <Button data-testid="picker-create-order-btn" onClick={commitNewOrder} className="h-11 shrink-0 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">
              <Plus className="mr-1 h-4 w-4" /> New
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OverrideRow({ label, ovG, setOvG, catalog, testid }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2">
      <span className="text-[11px] font-semibold uppercase text-slate-400">{label}</span>
      <div className="ml-auto flex items-center gap-2">
        <span className="flex items-center gap-1 font-mono text-[11px] text-slate-500" title="catalog value">
          <Info className="h-3 w-3" /> {catalog ? `${fmtNum(catalog, 1)}g` : "—"}
        </span>
        <Input
          data-testid={testid}
          inputMode="decimal"
          value={ovG}
          onChange={(e) => setOvG(e.target.value)}
          placeholder="override g"
          className="h-9 w-24 rounded-lg border-slate-700 bg-slate-950 text-right font-mono text-sm text-white"
        />
      </div>
    </div>
  );
}

function OutRow({ label, size, pcs, kg, testid }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2.5" data-testid={testid}>
      <span className="w-16 text-[11px] font-bold uppercase text-orange-300">{label}</span>
      <span className="font-mono text-xs text-slate-400">{size}</span>
      <div className="ml-auto flex items-center gap-4 font-mono text-sm">
        <span className="text-white">{fmtInt(pcs)}<span className="ml-0.5 text-[10px] text-slate-500">pcs</span></span>
        <span className="text-white">{fmtNum(kg, 1)}<span className="ml-0.5 text-[10px] text-slate-500">kg</span></span>
      </div>
    </motion.div>
  );
}
